'use client';

import { faXmark, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { DateTimePicker } from '@pickleballinc/ui/components/ui/datetime-picker';
import { Input } from '@pickleballinc/ui/components/ui/input';
import { Panel, PanelBody, PanelClose, PanelContent, PanelDescription, PanelHeader, PanelTitle } from '@pickleballinc/ui/components/ui/panel';
import { useState } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { ActionButtons } from '@/components/molecules';
import type { SessionFilterType } from '@/utils/scheduleFilters';
import { DatePreset, getDateRangeForPreset } from '@/utils/scheduleFilters';

interface SchedulerPrintSidebarProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onPrint: (data: SchedulerPrintFormType) => void;
	eventTitle?: string;
	minDate?: Date;
	maxDate?: Date;
	onSessionChange?: (value: SessionFilterType) => void;
	isFetchingScheduleData?: boolean;
	noMatches?: boolean;
	onDateRangeChange?: (data: { dateFrom?: Date; dateTo?: Date }) => void;
}

const schedulerPrintFormSchema = z.object({
	sessionSelection: z.enum(['all', 'morning', 'afternoon'] as const),
	dateFrom: z.date().optional(),
	dateTo: z.date().optional()
});

export type SchedulerPrintFormType = z.infer<typeof schedulerPrintFormSchema>;

export const SchedulerPrintSidebar = ({
	open,
	onOpenChange,
	onPrint,
	eventTitle = 'Schedule',
	minDate,
	maxDate,
	onSessionChange,
	isFetchingScheduleData,
	noMatches,
	onDateRangeChange
}: SchedulerPrintSidebarProps) => {
	const [selectedPreset, setSelectedPreset] = useState<DatePreset>('fullPeriod');

	const form = useForm<SchedulerPrintFormType>({
		resolver: zodResolver(schedulerPrintFormSchema),
		defaultValues: {
			sessionSelection: 'all',
			dateFrom: minDate,
			dateTo: maxDate
		}
	});

	// Reset form to full period when sidebar opens
	if (open && minDate && maxDate && (!form.getValues('dateFrom') || !form.getValues('dateTo'))) {
		form.reset({
			sessionSelection: 'all',
			dateFrom: minDate,
			dateTo: maxDate
		});
		setSelectedPreset('fullPeriod');
	}

	const handleDatePresetClick = (preset: DatePreset) => {
		setSelectedPreset(preset);

		const { dateFrom, dateTo } = getDateRangeForPreset(preset, minDate, maxDate);
		form.setValue('dateFrom', dateFrom);
		form.setValue('dateTo', dateTo);
		onDateRangeChange?.({ dateFrom, dateTo });
	};

	const handleDateChange = (callback: (date: Date | undefined) => void) => (date: Date | undefined) => {
		callback(date);
		// Reset to full period when manually changing dates
		if (selectedPreset !== 'fullPeriod') {
			setSelectedPreset('fullPeriod');
		}
		onDateRangeChange?.({ dateFrom: form.getValues('dateFrom'), dateTo: form.getValues('dateTo') });
	};

	const getPresetButtonClass = (preset: DatePreset) => {
		const baseClass = 'flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors border';
		const selectedClass = 'bg-orange-100 hover:bg-orange-200 text-orange-700 border-orange-300';
		const defaultClass = 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400';

		return `${baseClass} ${selectedPreset === preset ? selectedClass : defaultClass}`;
	};

	const handlePrint = form.handleSubmit((data: SchedulerPrintFormType) => {
		onPrint(data);
		onOpenChange(false);
	});

	const showStatusAlert = !isFetchingScheduleData && Boolean(noMatches);

	const submitTooltipMessage = isFetchingScheduleData
		? 'Loading schedule… please wait.'
		: noMatches
			? 'No matches to print for this period.'
			: undefined;

	return (
		<FormProvider {...form}>
			<Panel open={open} onOpenChange={onOpenChange}>
				<PanelContent className="flex w-full flex-col md:w-96 xl:w-100 2xl:w-120" aria-describedby="print-scheduler-description">
					<PanelHeader className="flex flex-col px-6 pb-2 pt-4 border-b border-gray-200">
						<div className="flex items-center justify-between">
							<PanelTitle asChild>
								<h2 className="text-2xl font-semibold text-gray-900">Print {eventTitle}</h2>
							</PanelTitle>
							<PanelClose asChild>
								<Button
									className="!size-8 !rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
									aria-label="Close dialog"
								>
									<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-700" />
								</Button>
							</PanelClose>
						</div>
						<PanelDescription className="text-sm font-normal text-gray-600 text-start">Select session to print</PanelDescription>
					</PanelHeader>

					<PanelBody className="flex-1 px-6 pt-6 pb-8">
						<div className="flex h-full flex-col">
							<div className="flex-1 space-y-8">
								{/* Session Selection */}
								<div className="space-y-4">
									<h3 className="text-base font-semibold text-gray-900">Session Type</h3>
									<div className="space-y-3">
										<label
											htmlFor="session-all"
											className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
										>
											<Input
												{...form.register('sessionSelection')}
												id="session-all"
												type="radio"
												value="all"
												onChange={() => onSessionChange?.('all')}
												className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500 shadow-none"
											/>
											<span className="text-sm font-medium text-gray-900">Full Day (Morning & Afternoon)</span>
										</label>
										<label
											htmlFor="session-morning"
											className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
										>
											<Input
												{...form.register('sessionSelection')}
												id="session-morning"
												type="radio"
												value="morning"
												onChange={() => onSessionChange?.('morning')}
												className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500 shadow-none"
											/>
											<span className="text-sm font-medium text-gray-900">Morning Session Only</span>
										</label>
										<label
											htmlFor="session-afternoon"
											className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
										>
											<Input
												{...form.register('sessionSelection')}
												id="session-afternoon"
												type="radio"
												value="afternoon"
												onChange={() => onSessionChange?.('afternoon')}
												className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500 shadow-none"
											/>
											<span className="text-sm font-medium text-gray-900">Afternoon Session Only</span>
										</label>
									</div>
								</div>

								{/* Date Range Selection */}
								<div className="space-y-4">
									<h3 className="text-base font-semibold text-gray-900">Date Range</h3>

									<div className="grid grid-cols-2 gap-4">
										<div className="space-y-2">
											<span className="text-sm font-medium text-gray-700">From Date</span>
											<Controller
												name="dateFrom"
												control={form.control}
												render={({ field }) => (
													<DateTimePicker
														value={field.value}
														onChange={handleDateChange(field.onChange)}
														placeholder="Start Date"
														granularity="date"
														className="w-full"
														minDate={minDate}
														maxDate={maxDate}
														theme="orange"
													/>
												)}
											/>
										</div>
										<div className="space-y-2">
											<span className="text-sm font-medium text-gray-700">To Date</span>
											<Controller
												name="dateTo"
												control={form.control}
												render={({ field }) => (
													<DateTimePicker
														value={field.value}
														onChange={handleDateChange(field.onChange)}
														placeholder="End Date"
														granularity="date"
														className="w-full"
														minDate={form.watch('dateFrom') || minDate}
														maxDate={maxDate}
														theme="orange"
													/>
												)}
											/>
										</div>
									</div>

									{/* Quick Date Presets */}
									<div className="flex gap-2">
										<Button onClick={() => handleDatePresetClick('fullPeriod')} className={getPresetButtonClass('fullPeriod')}>
											Full Period
										</Button>
										<Button onClick={() => handleDatePresetClick('today')} className={getPresetButtonClass('today')}>
											Today
										</Button>
										<Button onClick={() => handleDatePresetClick('thisWeek')} className={getPresetButtonClass('thisWeek')}>
											This Week
										</Button>
									</div>
								</div>

								{/* Status Alert: no matches or loading */}
								{Boolean(showStatusAlert) && (
									<div className="items-center flex gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
										<FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 size-4 text-warning-600" />
										<span>No matches for the selected period. Try a different date range or session.</span>
									</div>
								)}
							</div>

							<ActionButtons
								className="flex flex-row gap-4 mt-1"
								closeButtonClassName="w-full"
								submitButtonClassName="w-full"
								onClose={() => onOpenChange(false)}
								onSubmit={handlePrint}
								submitButtonText="Print"
								closeButtonText="Close"
								submitButtonDisabled={!!isFetchingScheduleData || !!noMatches}
								submitTooltipMessage={submitTooltipMessage}
							/>
						</div>
					</PanelBody>
				</PanelContent>
			</Panel>
		</FormProvider>
	);
};
